import { cn } from "@/lib/utils";
import BgCircles from "@/assets/images/ci-app/bg-circles";
import { useState } from "react";
import SchedulingUI1 from "@/assets/images/ci-app/scheduling/scheduling-ui-1.png";
import SchedulingUI2 from "@/assets/images/ci-app/scheduling/scheduling-ui-2.png";
import SchedulingUI3 from "@/assets/images/ci-app/scheduling/scheduling-ui-3.png";
import ComplianceUI1 from "@/assets/images/ci-app/compliance/compliance-ui-1.png";
import ComplianceUI2 from "@/assets/images/ci-app/compliance/compliance-ui-2.png";
import SupportUI1 from "@/assets/images/ci-app/support/support-ui-1.png";
import SupportUI2 from "@/assets/images/ci-app/support/support-ui-2.png";
import SupportUI3 from "@/assets/images/ci-app/support/support-ui-3.png";
import InvoicingUI1 from "@/assets/images/ci-app/invoicing/invoicing-ui-1.png";
import InvoicingUI2 from "@/assets/images/ci-app/invoicing/invoicing-ui-2.png";
import Image from "next/image";

export interface BenefitsCardProps {
  title: string;
  subtitle: string;
  body: string;
  className?: string;
}

const BenefitsCard: React.FC<BenefitsCardProps> = ({
  title,
  subtitle,
  body,
  className,
}) => {
  const [isActive, setIsActive] = useState(false);

  return (
    <div
      className={cn(
        "group relative col-span-1 flex h-[336px] w-[281px] flex-col overflow-hidden rounded-3xl bg-secondary-500 p-6 ring-[1px] ring-neutral-200",
        "lg:cursor-default",
        className
      )}
      style={{
        boxShadow:
          "4px 8px 32px 0px rgba(255, 213, 191, 0.7), -4px -8px 32px 0px rgba(196, 244, 245, 0.7)",
      }}
      onClick={() => {
        if (window.innerWidth < 1024) {
          setIsActive(!isActive);
        }
      }}
    >
      <div className="absolute inset-0">
        <BgCircles className="absolute -top-[35.17px] h-full w-full" />
      </div>
      <div
        className={cn(
          "relative flex flex-col font-montserrat text-subheading-3-500 text-white transition-opacity duration-300",
          "lg:opacity-0 lg:group-hover:opacity-100",
          isActive ? "opacity-100" : "opacity-0"
        )}
      >
        {body}
      </div>
      <div className="flex flex-col gap-2 opacity-100">
        {subtitle === "Scheduling" && (
          <div
            className={cn(
              "absolute -inset-y-[80px] inset-x-[73px] animate-scheduling transition-transform duration-[400ms]",
              "lg:group-hover:-translate-y-48 lg:group-hover:translate-x-36 lg:group-hover:scale-50",
              isActive && "-translate-y-48 translate-x-36 scale-50"
            )}
          >
            <Image
              src={SchedulingUI3}
              alt={`${subtitle} UI preview 1`}
              className="absolute left-[45.03px] top-[39.86px] h-[107.58px] min-w-[148px] rounded-2xl shadow-[0px_0px_9.87px_0px_#FFFFFF80]"
            />
            <Image
              src={SchedulingUI2}
              alt={`${subtitle} UI preview 2`}
              className="absolute left-[10.8px] top-[99.03px] h-[107.58px] min-w-[148px] rounded-2xl shadow-[0px_0px_9.87px_0px_#FFFFFF80]"
            />
            <Image
              src={SchedulingUI1}
              alt={`${subtitle} UI preview 3`}
              className="absolute -left-[28.2px] top-[159.48px] h-[107.58px] min-w-[148px] rounded-2xl shadow-[0px_0px_9.87px_0px_#FFFFFF80]"
            />
          </div>
        )}
        {subtitle === "Compliance" && (
          <div className="absolute -inset-y-[70px] inset-x-[73px]">
            <Image
              src={ComplianceUI2}
              alt={`${subtitle} UI preview 1`}
              className={cn(
                "absolute -left-[37px] top-[97px] h-[77px] min-w-[274px] animate-compliance-ui1 rounded-3xl shadow-[0px_0px_9.87px_0px_#FFFFFF80] transition-transform duration-500",
                "lg:group-hover:-translate-x-52 lg:group-hover:-translate-y-24 lg:group-hover:scale-50",
                isActive && "-translate-x-52 -translate-y-24 scale-50"
              )}
            />
            <Image
              src={ComplianceUI1}
              alt={`${subtitle} UI preview 2`}
              className={cn(
                "absolute -left-[55px] top-[166px] h-[91px] min-w-[320px] animate-compliance-ui2 rounded-3xl shadow-[0px_0px_9.87px_0px_#FFFFFF80] transition-transform duration-500",
                "lg:group-hover:translate-x-52 lg:group-hover:translate-y-24 lg:group-hover:scale-50",
                isActive && "translate-x-52 translate-y-24 scale-50"
              )}
            />
          </div>
        )}
        {subtitle === "Support" && (
          <div className="absolute -inset-y-[70px] inset-x-[73px]">
            <Image
              src={SupportUI1}
              alt={`${subtitle} UI preview 1`}
              className={cn(
                "absolute left-[50.3px] top-[88.01px] h-[33.01px] min-w-[162.98px] animate-support-ui1 rounded-bl-[25.79px] rounded-tl-[25.79px] rounded-tr-[25.79px] shadow-[0px_0px_9.87px_0px_#FFFFFF80] transition-transform duration-500",
                "lg:group-hover:translate-x-52 lg:group-hover:scale-50",
                isActive && "translate-x-52 scale-50"
              )}
            />
            <Image
              src={SupportUI2}
              alt={`${subtitle} UI preview 2`}
              className={cn(
                "absolute -left-[38.45px] top-[221.39px] h-[34.98px] min-w-[228.27px] animate-support-ui2 rounded-[46.02px] shadow-[0px_0px_9.87px_0px_#FFFFFF80] transition-transform duration-500",
                "lg:group-hover:-translate-x-52 lg:group-hover:-translate-y-12 lg:group-hover:scale-50",
                isActive && "-translate-x-52 -translate-y-12 scale-50"
              )}
            />
            <Image
              src={SupportUI3}
              alt={`${subtitle} UI preview 3`}
              className={cn(
                "absolute -left-[54px] top-[135px] h-[75.13px] min-w-[396.92px] animate-support-ui3 rounded-[3.94px] shadow-[0px_0px_9.87px_0px_#FFFFFF80] transition-transform duration-500",
                "lg:group-hover:translate-x-52 lg:group-hover:scale-50",
                isActive && "translate-x-52 scale-50"
              )}
            />
          </div>
        )}
        {subtitle === "Invoicing" && (
          <div className="absolute -inset-y-[70px] inset-x-[73px]">
            <Image
              src={InvoicingUI1}
              alt={`${subtitle} UI preview 1`}
              className={cn(
                "absolute -left-[55px] top-[105px] h-[111.65px] min-w-[403.65px] animate-invoicing-ui1 rounded-3xl shadow-[0px_0px_9.87px_0px_#FFFFFF80] transition-transform duration-500",
                "lg:group-hover:-translate-y-56 lg:group-hover:translate-x-40 lg:group-hover:scale-50",
                isActive && "-translate-y-56 translate-x-40 scale-50"
              )}
            />
            <Image
              src={InvoicingUI2}
              alt={`${subtitle} UI preview 2`}
              className={cn(
                "absolute left-[63.52px] top-[188.29px] h-[68.71px] min-w-[328.03px] animate-invoicing-ui2 rounded-2xl shadow-[0px_0px_9.87px_0px_#FFFFFF80] transition-transform duration-500",
                "lg:group-hover:-translate-x-96 lg:group-hover:-translate-y-6 lg:group-hover:scale-50",
                isActive && "-translate-x-96 -translate-y-6 scale-50"
              )}
            />
          </div>
        )}
      </div>
      <div className="relative mt-auto flex flex-col gap-1">
        <div className="font-montserrat text-xs font-semibold uppercase tracking-[.26em] text-primary-200">
          {subtitle}
        </div>
        <div className="font-montserrat text-heading-3 text-white">{title}</div>
      </div>
    </div>
  );
};

export default BenefitsCard;
